import type {ErrorBoundaryCaptureInfo, ParsedBoundaryError} from './types';
import {BoundarySource} from './types';

/**
 * Serializes a client boundary error to a JSON string.
 * @param error - The error to serialize.
 * @returns The serialized error.
 */
export function serializeClientBoundaryError(error: Error): string {
  return JSON.stringify({
    error: {message: error.message, stack: error.stack},
    context: {source: BoundarySource.CLIENT, boundary: 'component'},
  });
}

/**
 * Parses a serialized boundary error into a ParsedBoundaryError object.
 * @param rawError - The serialized error to parse.
 * @returns The parsed boundary error.
 */
export function parseBoundaryError(rawError: string): ParsedBoundaryError {
  try {
    const parsed = JSON.parse(rawError);
    const parsedError = parsed?.error ?? {};
    const parsedContext = parsed?.context ?? {};

    return {
      message: parsedError.message || parsed.message || rawError,
      stack: parsedError.stack || parsed.stack,
      source:
        parsedContext.source === BoundarySource.SERVER
          ? BoundarySource.SERVER
          : BoundarySource.CLIENT,
      boundary: parsedContext.boundary,
      digest: parsedError.digest,
    };
  } catch {
    return {
      message: rawError,
      source: BoundarySource.CLIENT,
    };
  }
}

/**
 * Creates an error instance from a ParsedBoundaryError object.
 * @param parsedError - The parsed boundary error.
 * @returns The error instance.
 */
export function createErrorInstance(parsedError: ParsedBoundaryError): Error {
  const error = new Error(parsedError.message);

  if (parsedError.stack) {
    error.stack = parsedError.stack;
  }

  return error;
}

/**
 * Creates a capture info object from a ParsedBoundaryError object.
 * @param parsedError - The parsed boundary error.
 * @returns The capture info object.
 */
export function createCaptureInfoFromParsedError(
  parsedError: ParsedBoundaryError,
): ErrorBoundaryCaptureInfo {
  return {
    tags: {
      source: parsedError.source,
      ...(parsedError.boundary ? {boundary: parsedError.boundary} : {}),
      ...(parsedError.digest ? {digest: parsedError.digest} : {}),
    },
    extra: {
      stack: parsedError.stack,
      ...(parsedError.digest ? {digest: parsedError.digest} : {}),
    },
  };
}

/**
 * Creates a capture info object for a component.
 * @param errorInfo - The error info.
 * @returns The capture info object.
 */
export function createComponentCaptureInfo(
  errorInfo: unknown,
): ErrorBoundaryCaptureInfo {
  return {
    tags: {
      source: BoundarySource.CLIENT,
      boundary: 'component',
    },
    extra: {
      errorInfo,
    },
  };
}
